import React, { FormEvent, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import Input from "../UI/Input";
import Button from "../UI/Button";
import Loader from "../UI/Loader";
import ValidatedInput from "../UI/ValidatedInput";
import ValidatedSelect from "../UI/ValidatedSelect";
import { format, isMatch } from "date-fns";
import FormEditor from "../Form/Editor/FormEditor";
import { formatJSONDateWithoutTime } from "../../utils/dateHelpers";
import { Link, useNavigate } from "react-router-dom";
import { ConfiguredRequiredField, createClient, createHouseholdMember, deleteHouseholdMember, getClientById, getConfiguredRequiredFields, getEthnicities, getGenders, getPossibleDuplicateClients, getRaces, getRelationships, makeHouseholdMemberHeadOfHousehold, searchClients, updateClient, updateHouseholdMember } from "../../services/clientsService";
import Relationship from "../../models/serviceModels/Relationship";
import Gender from "../../models/serviceModels/Gender";
import Race from "../../models/serviceModels/Race";
import Client from "../../models/serviceModels/Client";
import { getClientForm, getClientFormInstance } from "../../services/formsService";
import Form from "../../models/serviceModels/Form";
import FormInstance from "../../models/serviceModels/FormInstance";
import EditableFormField from "../../models/pageModels/EditableFormField";
import HouseholdMember from "../../models/serviceModels/HouseholdMember";
import CreateOrEditClientModel from "../../models/pageModels/CreateOrEditClientModel";
import CreateOrEditHouseholdMemberModel from "../../models/pageModels/CreateOrEditHouseholdMemberModel";
import BackButton from "../UI/BackButton";
import ApiErrorMessages from "../UI/ApiErrorMessages";
import Select from "../UI/Select";
import Modal from "../UI/Modal";
import Dropdown from "../UI/Dropdown";
import Ethnicity from "../../models/serviceModels/Ethnicity";
import CheckboxGroup from "../UI/CheckboxGroup";
import Checkbox from "../UI/Checkbox";
import states from "../../constants/states";
import RadioButton from "../UI/RadioButton";
import { searchPrograms } from "../../services/programsService";
import PagedData from "../../models/serviceModels/PagedData";
import Program from "../../models/serviceModels/Program";
import ErrorMessages from "../UI/ErrorMessages";
import toast from "react-hot-toast";
import PossibleDuplicateClient from "../../models/serviceModels/PossibleDuplicateClient";
import getclassNames from "../../utils/getClassNames";
import { configuredRequiredFieldsOrgSettingsEnabled, householdEditorEnabled, householdIdentifiersEnabled, householdProgramEnrollmentEnabled, linkedHouseholdParticipantsEnabled, optionalDateOfBirthEnabled } from "../../utils/features";
import { navigateToReturnLocation } from "../../utils/locationReturnService";
import { OrganizationContext } from "../../App";
import { HouseholdMemberParticipantMode } from "../../models/serviceModels/Organization";
import { normalizeFormInstanceFieldValue } from "../../mappers/formFieldMapper";
import {
    getDemographicsRaceMode,
    getEthnicityFieldVisible,
    getGenderOptionsForOrganization,
    getRaceFieldLabel,
    getRaceOptionsForOrganization,
    getStoredGenderValueForOrganization,
    getStoredRaceSelectionsForOrganization,
    isDemographicVisible,
    splitRaceSelectionsForOrganization
} from "../../utils/demographicsSchema";
import "../UI/LinkedHouseholdNotice.css";
import "./ParticpantForm.css";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const getParticipantDefaultErrors = () => {
    return {
        firstName: [],
        lastName: [],
        birthdate: [],
        race: [],
        ethnicity: [],
        gender: [],
        pronouns: [],
        address: [],
        city: [],
        state: [],
        zip: [],
        phone: [],
        email: [],
        enrolled: [],
        activated: []
    };
}

const getHouseholdMemberDefaultErrors = () => {
    return {
        firstName: [],
        lastName: [],
        birthdate: [],
        race: [],
        ethnicity: [],
        gender: [],
        relationship: [],
        linkedParticipant: [],
        createParticipant: []
    };
}

interface ParticipantFormProps {
    id?: string;
    existingModel?: CreateOrEditClientModel;
    existingEditableFormFields?: EditableFormField[];
    onSave?: () => void;
    publicFormInstanceId?: string;
    columnNumber?: number;
    isEmbedded?: boolean;
    mode?: 'participant' | 'household';
}

const getDefaultModel = (): CreateOrEditClientModel => {
    return {
        id: '',
        firstName: '',
        lastName: '',
        created: '',
        dateOfBirth: '',
        genderId: '',
        ethnicityId: '',
        raceIds: [],
        pronouns: '',
        middleName: '',
        addressCity: '',
        addressLine1: '',
        addressLine2: '',
        addressState: '',
        addressZipCode: '',
        deletedHouseholdMembers: [],
        householdMembers: [],
        phoneNumber: '',
        email: '',
        activated: format(new Date(), 'yyyy-MM-dd'),
        enrolled: format(new Date(), 'yyyy-MM-dd'),
        errors: getParticipantDefaultErrors(),
        selectedPrograms: []
    };
}

const getDefaultHouseholdMember = (
    linkExistingParticipant = false,
    householdMemberParticipantMode: HouseholdMemberParticipantMode = 'defaultUnchecked'
): CreateOrEditHouseholdMemberModel => {
    return {
        id: '',
        firstName: '',
        middleName: '',
        lastName: '',
        dateOfBirth: '',
        ethnicityId: '',
        raceIds: [],
        genderId: '',
        relationshipId: '',
        email: '',
        phoneNumber: '',
        linkExistingParticipant,
        linkedClientId: '',
        createParticipant: householdMemberParticipantMode === 'defaultChecked' || householdMemberParticipantMode === 'alwaysFullParticipant',
        errors: getHouseholdMemberDefaultErrors()
    };
};

const cloneHouseholdMember = (member: CreateOrEditHouseholdMemberModel): CreateOrEditHouseholdMemberModel => {
    return {
        ...member,
        raceIds: [...(member.raceIds || [])],
        errors: getHouseholdMemberDefaultErrors()
    };
};

const getComparableHouseholdMember = (member: CreateOrEditHouseholdMemberModel) => {
    return {
        id: member.id || '',
        firstName: member.firstName || '',
        middleName: member.middleName || '',
        lastName: member.lastName || '',
        dateOfBirth: member.dateOfBirth || '',
        ethnicityId: member.ethnicityId || '',
        raceIds: (member.raceIds || []).map(r => r.value).sort(),
        genderId: member.genderId || '',
        relationshipId: member.relationshipId || '',
        linkExistingParticipant: !!member.linkExistingParticipant,
        linkedClientId: member.linkedClientId || '',
        createParticipant: !!member.createParticipant,
        phoneNumber: member.phoneNumber || '',
        email: member.email || ''
    };
};

const householdMembersAreEqual = (
    member: CreateOrEditHouseholdMemberModel,
    originalMember?: CreateOrEditHouseholdMemberModel
) => {
    if (!originalMember) {
        return false;
    }

    return JSON.stringify(getComparableHouseholdMember(member)) === JSON.stringify(getComparableHouseholdMember(originalMember));
};

const ParticipantForm = (props: ParticipantFormProps) => {
    const [model, setModel] = useState<CreateOrEditClientModel>(props.existingModel || getDefaultModel());
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [genderOptions, setGenderOptions] = useState<Gender[]>([]);
    const [racesOptions, setRacesOptions] = useState<Race[]>([]);
    const [ethnicityOptions, setEthnicityOptions] = useState<Ethnicity[]>([]);
    const [existingFields, setExistingFields] = useState<EditableFormField[]>(props.existingEditableFormFields || []);
    const [fields, setFields] = useState<EditableFormField[]>([]);
    const [form, setForm] = useState<Form | null>();
    const [formInstance, setFormInstance] = useState<FormInstance | null>(null);
    const [relationshipOptions, setRelationshipOptions] = useState<Relationship[]>([]);
    const [submitError, setSubmitError] = useState<any>();
    const [submittingAndCreateNew, setSubmittingAndCreateNew] = useState(false);
    const firstNameRef = useRef<HTMLInputElement>();
    const [programs, setPrograms] = useState<Program[]>([]);
    const loaded = useRef(false);
    const [showPrograms, setShowPrograms] = useState(false);
    const [checkingDuplicate, setCheckingDuplicate] = useState(false);
    const [possibleDuplicates, setPossibleDuplicates] = useState<PossibleDuplicateClient[]>([]);
    const [duplicateError, setDuplicateError] = useState<any>();
    const [showingPossibleDuplicates, setShowingPossibleDuplicates] = useState(false);
    const duplicateDebounce = useRef<any>();
    const [highlightDuplicateBackgrounds, setHighlightDuplicateBackgrounds] = useState(false);
    const [householdMemberLinkQueries, setHouseholdMemberLinkQueries] = useState<{ [key: number]: string }>({});
    const [householdMemberLinkResults, setHouseholdMemberLinkResults] = useState<{ [key: number]: Client[] }>({});
    const [householdMemberLinkResultsVisible, setHouseholdMemberLinkResultsVisible] = useState<{ [key: number]: boolean }>({});
    const [householdMemberLinkLoading, setHouseholdMemberLinkLoading] = useState<{ [key: number]: boolean }>({});
    const [householdMemberLinkError, setHouseholdMemberLinkError] = useState<{ [key: number]: string }>({});
    const [linkedParticipantsById, setLinkedParticipantsById] = useState<{ [key: string]: Client }>({});
    const [makingHeadOfHouseholdMemberId, setMakingHeadOfHouseholdMemberId] = useState<string>('');
    const [programEnrollmentSelections, setProgramEnrollmentSelections] = useState<{ [key: string]: { selected: boolean, enrollmentDate: string } }>({});
    const [householdMemberEditorIndex, setHouseholdMemberEditorIndex] = useState<number | undefined>();
    const [householdMemberEditorOriginal, setHouseholdMemberEditorOriginal] = useState<CreateOrEditHouseholdMemberModel | undefined>();
    const [configuredRequiredFields, setConfiguredRequiredFields] = useState<ConfiguredRequiredField[]>([]);
    const [linkedHouseholdOwners, setLinkedHouseholdOwners] = useState<Array<{
        id: string,
        householdId?: number,
        middleName?: string,
        firstName: string,
        lastName: string,
        birthDate: string | null,
        members: HouseholdMember[]
    }>>([]);
    const originalHouseholdMembersById = useRef<{ [key: string]: CreateOrEditHouseholdMemberModel }>({});

    const navigate = useNavigate();
    const organization = useContext(OrganizationContext);
    const isHouseholdOnlyMode = props.mode === 'household';
    const showHouseholdEditingSection = isHouseholdOnlyMode || !props.id || !householdEditorEnabled;
    const useReturnLocation = !!props.id || isHouseholdOnlyMode;
    const showPronouns = isDemographicVisible(organization.value, 'pronouns');
    const showGender = isDemographicVisible(organization.value, 'gender');
    const showEthnicity = getEthnicityFieldVisible(organization.value);
    const showRace = isDemographicVisible(organization.value, 'race');
    const showAddress = isDemographicVisible(organization.value, 'address');
    const showCity = isDemographicVisible(organization.value, 'city');
    const showState = isDemographicVisible(organization.value, 'state');
    const showZip = isDemographicVisible(organization.value, 'zip');
    const showPhone = isDemographicVisible(organization.value, 'phone');
    const showEmail = isDemographicVisible(organization.value, 'email');
    const raceFieldLabel = getRaceFieldLabel(organization.value);
    const genderSelectOptions = getGenderOptionsForOrganization(genderOptions, organization.value);
    const raceSelectOptions = getRaceOptionsForOrganization(racesOptions, ethnicityOptions, organization.value);
    const isCombinedRaceMode = getDemographicsRaceMode(organization.value) === 'combined';
    const primaryProgramEnrollmentKey = 'primary';

    const getRequiredFieldEffectiveDate = (effectiveAt?: string) => {
        return (effectiveAt || format(new Date(), 'yyyy-MM-dd')).slice(0, 10);
    };

    const isConfiguredFieldRequired = (fieldName: string, effectiveAt?: string) => {
        const effectiveDate = getRequiredFieldEffectiveDate(effectiveAt);
        const matchingFields = configuredRequiredFields
            .filter(field => field.fieldName === fieldName && (!field.effectiveDate || field.effectiveDate <= effectiveDate))
            .sort((a, b) => (b.effectiveDate || '').localeCompare(a.effectiveDate || ''));

        return !!matchingFields[0];
    };

    const dateOfBirthRequired = configuredRequiredFieldsOrgSettingsEnabled
        ? isConfiguredFieldRequired('dateOfBirth', model.created)
        : !optionalDateOfBirthEnabled;

    const isHouseholdMemberDateOfBirthRequired = (member?: CreateOrEditHouseholdMemberModel) => {
        return configuredRequiredFieldsOrgSettingsEnabled
            ? isConfiguredFieldRequired('dateOfBirth', member?.created)
            : !optionalDateOfBirthEnabled;
    };
    const isProfileFieldVisible = (fieldName: string) => {
        const visibleFields: Record<string, boolean> = {
            race: showRace,
            ethnicity: showEthnicity,
            gender: showGender,
            pronouns: showPronouns,
            address: showAddress,
            city: showCity,
            state: showState,
            zip: showZip,
            phone: showPhone,
            email: showEmail
        };

        return visibleFields[fieldName] ?? true;
    };

    const isProfileFieldRequired = (fieldName: string) => isProfileFieldVisible(fieldName)
        && isConfiguredFieldRequired(fieldName, model.created);

    const logUnexpectedSubmitError = (error: unknown) => {
        if (axios.isAxiosError(error) && error.response?.status === 400) {
            return;
        }

        console.error(error);
    };

    const getRequestEthnicityId = (
        raceSelection: { ethnicityId?: number },
        fallbackEthnicityId: string
    ): number | null | undefined => {
        if (isCombinedRaceMode) {
            return raceSelection.ethnicityId || null;
        }

        return (fallbackEthnicityId && Number(fallbackEthnicityId)) || undefined;
    };

    const returnToEditOrigin = () => {
        navigateToReturnLocation(navigate, () => {
            if (props.id) {
                navigate(`/participants/${props.id}/profile-demographics`);
                return;
            }

            navigate(-1);
        }, {
            replace: true
        });
    };

    useEffect(() => {
        setLoading(true);
        setLinkedHouseholdOwners([]);

        const promises: [Promise<Race[]>, Promise<Gender[]>, Promise<Form>, Promise<Relationship[]>, Promise<Ethnicity[]>, Promise<PagedData<Program>>, Promise<ConfiguredRequiredField[]>, Promise<Client>?, Promise<FormInstance>?] = [
            getRaces(),
            getGenders(),
            getClientForm(),
            getRelationships(),
            getEthnicities(),
            props.id ? Promise.resolve({ // we don't need to get the programs if we are just editing an existing participant
                data: [],
                totalCount: 0
            }) : searchPrograms({
                take: 1000
            }),
            configuredRequiredFieldsOrgSettingsEnabled ? getConfiguredRequiredFields() : Promise.resolve([])
        ];

        if (props.id) {
            promises.push(getClientById({ id: props.id }));
            promises.push(getClientFormInstance(props.id));
        }

        Promise.all(promises).then(([races, genders, clientForm, relationships, ethnicities, programsPage, requiredFields, client, clientFormInstance]) => {
            setGenderOptions([...genders]);
            setRacesOptions([...races]);
            setRelationshipOptions([...relationships]);
            setEthnicityOptions([...ethnicities]);
            setPrograms([...programsPage.data]);
            setConfiguredRequiredFields([...requiredFields]);

            if (clientForm) {
                if (props.publicFormInstanceId) {
                    setFields([
                        ...clientForm.fields.map((f: any) => {
                            const matchingExistingField = existingFields.find(ef => ef.name === f.name);
                            return {
                                ...f,
                                value: matchingExistingField?.value || '',
                                files: matchingExistingField?.files || []
                            };
                        })
                    ]);
                } else {
                    setFields([
                        ...clientForm.fields.map((f: any) => {
                            return {
                                ...f,
                                value: '',
                                files: []
                            };
                        })
                    ]);
                }
                setForm({ ...clientForm });
            }

            if (client) {
                model.id = client.id;
                model.firstName = client.firstName;
                model.middleName = client.middleName || '';
                model.lastName = client.lastName;
                model.created = client.created;
                model.dateOfBirth = formatJSONDateWithoutTime(client.dateOfBirth, 'yyyy-MM-dd');
                model.ethnicityId = client.ethnicityId?.toString() || '';
                model.genderId = getStoredGenderValueForOrganization({
                    genderId: client.genderId,
                    genderName: client.genderName,
                    genders
                }, organization.value);
                model.addressLine1 = client.addressLine1 || '';
                model.addressLine2 = client.addressLine2 || '';
                model.pronouns = client.pronouns || '';
                model.addressCity = client.addressCity || '';
                model.addressState = client.addressState || '';
                model.addressZipCode = client.addressZipCode || '';
                model.phoneNumber = client.phoneNumber || '';
                model.raceIds = getStoredRaceSelectionsForOrganization({
                    ethnicityId: client.ethnicityId,
                    ethnicityName: client.ethnicityName,
                    races: client.races
                }, organization.value).filter(r => r !== null);
                model.email = client.email || '';
                model.householdId = client.householdId;
                model.householdIds = client.householdIds;
                const loadedHouseholdMembers = client.householdMembers.map((hhm: HouseholdMember) => {
                    return {
                        id: hhm.id,
                        firstName: hhm.firstName,
                        middleName: hhm.middleName || '',
                        lastName: hhm.lastName,
                        created: hhm.created,
                        dateOfBirth: formatJSONDateWithoutTime(hhm.dateOfBirth, 'yyyy-MM-dd'),
                        genderId: getStoredGenderValueForOrganization({
                            genderId: hhm.genderId,
                            genderName: hhm.genderName,
                            genders
                        }, organization.value),
                        ethnicityId: hhm.ethnicityId?.toString() || '',
                        email: hhm.email,
                        phoneNumber: hhm.phoneNumber,
                        raceIds: getStoredRaceSelectionsForOrganization({
                            ethnicityId: hhm.ethnicityId,
                            ethnicityName: hhm.ethnicityName,
                            races: hhm.races
                        }, organization.value),
                        relationshipId: hhm.relationshipId?.toString() || '',
                        linkExistingParticipant: !!hhm.linkedClientId,
                        linkedClientId: hhm.linkedClientId,
                        createParticipant: false,
                        errors: getHouseholdMemberDefaultErrors()
                    }
                });
                model.householdMembers = loadedHouseholdMembers;
                originalHouseholdMembersById.current = loadedHouseholdMembers.reduce((previousValue: { [key: string]: CreateOrEditHouseholdMemberModel }, currentValue) => {
                    if (currentValue.id) {
                        previousValue[currentValue.id] = cloneHouseholdMember(currentValue);
                    }

                    return previousValue;
                }, {});

                setModel({ ...model });
                setLinkedHouseholdOwners((client.linkedHouseholds || []).map(linkedHousehold => {
                    return {
                        id: linkedHousehold.owner.clientId,
                        householdId: linkedHousehold.householdId || linkedHousehold.owner.householdId,
                        firstName: linkedHousehold.owner.firstName,
                        middleName: linkedHousehold.owner.middleName,
                        lastName: linkedHousehold.owner.lastName,
                        birthDate: linkedHousehold.owner.birthDate,
                        members: linkedHousehold.members
                    };
                }));
            }

            if (clientFormInstance) {
                setFields([
                    ...clientForm.fields.map((cf: any) => {
                        const existingValue = clientFormInstance.instanceFields.find((inf: any) => inf.name === cf.name);
                        return {
                            ...cf,
                            instanceFieldId: existingValue?.id,
                            value: existingValue?.value || '',
                            files: existingValue?.files || []
                        };
                    })
                ]);

                setFormInstance(clientFormInstance);
            }
        }).finally(() => {
            setLoading(false);
            loaded.current = true;
        });
    }, [props.id]);

    useEffect(() => {
        if (props.id) {
            return;
        }

        if (duplicateDebounce.current) {
            clearTimeout(duplicateDebounce.current);
        }

        duplicateDebounce.current = setTimeout(() => {
            setDuplicateError(undefined);
            setPossibleDuplicates([]);
            setShowingPossibleDuplicates(false);
            setCheckingDuplicate(false);


            if (!model.firstName || !model.lastName || (dateOfBirthRequired && !model.dateOfBirth) || (model.dateOfBirth && !isMatch(model.dateOfBirth, 'yyyy-MM-dd'))) {
                return;
            }

            setCheckingDuplicate(true);

            const firstName = model.firstName;
            const lastName = model.lastName;
            const birthDate = model.dateOfBirth;

            getPossibleDuplicateClients({
                firstName,
                lastName,
                birthDate
            }).then(r => {
                if (model.firstName !== firstName || model.lastName !== lastName || model.dateOfBirth !== birthDate) {
                    return;
                }

                setPossibleDuplicates(r);
            }).catch(e => {
                if (model.firstName !== firstName || model.lastName !== lastName || model.dateOfBirth !== birthDate) {
                    return;
                }

                setDuplicateError(e);
            }).finally(() => {
                setCheckingDuplicate(false);
            })
        }, 500);

        return () => {
            if (duplicateDebounce.current) {
                clearTimeout(duplicateDebounce.current);
                duplicateDebounce.current = undefined;
            }
        }
    }, [dateOfBirthRequired, model.firstName, model.lastName, model.dateOfBirth, props.id]);

    useEffect(() => {
        if (!loading && firstNameRef.current) {
            firstNameRef.current.focus();
        }
    }, [loading]);

    const reset = () => {
        setModel(getDefaultModel());
        setFields([
            ...fields.map(f => {
                return {
                    ...f,
                    value: '',
                    files: []
                };
            })
        ]);

        if (firstNameRef.current) {
            firstNameRef.current.focus();
        }

        setShowPrograms(false);
    };

    const handleOnFieldsChange = (fields: any[]) => {
        setFields([
            ...fields
        ]);
    };

    const getHouseholdProgramEnrollmentKey = (index: number) => `household-${index}`;

    const getProgramEnrollmentSelection = (key: string) => {
        return programEnrollmentSelections[key] || {
            selected: key === primaryProgramEnrollmentKey,
            enrollmentDate: model.enrolled
        };
    };

    const householdMemberIsDeleted = (member: CreateOrEditHouseholdMemberModel) => {
        return !!member.id && model.deletedHouseholdMembers.includes(member.id);
    };

    const getOriginalHouseholdMember = (member: CreateOrEditHouseholdMemberModel) => {
        return member.id ? originalHouseholdMembersById.current[member.id] : undefined;
    };

    const householdMemberIsUpdated = (member: CreateOrEditHouseholdMemberModel) => {
        return !!member.id && !householdMemberIsDeleted(member) && !householdMembersAreEqual(member, getOriginalHouseholdMember(member));
    };

    const getHouseholdMemberChangeStatus = (member: CreateOrEditHouseholdMemberModel) => {
        if (householdMemberIsDeleted(member)) {
            return 'Removed';
        }

        if (!member.id) {
            return 'New';
        }

        if (householdMemberIsUpdated(member)) {
            return 'Modified';
        }

        return '';
    };

    const getProgramEnrollmentParticipantRows = () => {
        const currentParticipantName = [model.firstName, model.middleName, model.lastName]
            .filter(v => !!v)
            .join(' ')
            .trim() || 'Current participant';

        return [
            {
                key: primaryProgramEnrollmentKey,
                label: currentParticipantName,
                type: 'Current Participant',
                householdMemberIndex: undefined
            },
            ...(householdProgramEnrollmentEnabled ? model.householdMembers : [])
                .map((member, index) => {
                    if (householdMemberIsDeleted(member)) {
                        return null;
                    }

                    const isEligible = linkedHouseholdParticipantsEnabled
                        && (!!member.linkedClientId || (!member.linkExistingParticipant && !!member.createParticipant));
                    if (!isEligible) {
                        return null;
                    }

                    const name = [member.firstName, member.middleName, member.lastName]
                        .filter(v => !!v)
                        .join(' ')
                        .trim() || `Household Member ${index + 1}`;

                    return {
                        key: getHouseholdProgramEnrollmentKey(index),
                        label: name,
                        type: member.linkedClientId ? 'Linked Participant' : 'Create as Participant',
                        householdMemberIndex: index
                    };
                })
                .filter((row): row is { key: string, label: string, type: string, householdMemberIndex: number } => !!row)
        ];
    };

    const handleProgramEnrollmentParticipantSelected = (key: string, selected: boolean) => {
        const existingSelection = getProgramEnrollmentSelection(key);
        setProgramEnrollmentSelections({
            ...programEnrollmentSelections,
            [key]: {
                ...existingSelection,
                selected
            }
        });
    };

    const handleProgramEnrollmentDateChanged = (key: string, enrollmentDate: string) => {
        const existingSelection = getProgramEnrollmentSelection(key);
        setProgramEnrollmentSelections({
            ...programEnrollmentSelections,
            [key]: {
                ...existingSelection,
                enrollmentDate
            }
        });
    };

    const getSelectedProgramEnrollmentRows = () => {
        if (!model.selectedPrograms || model.selectedPrograms.length === 0) {
            return [];
        }

        return getProgramEnrollmentParticipantRows().filter(row => getProgramEnrollmentSelection(row.key).selected);
    };

    const handleOnPrint = () => {
        window.print();
    };

    const validateHouseholdMember = (member: CreateOrEditHouseholdMemberModel) => {
        let isValid = true;
        member.errors = getHouseholdMemberDefaultErrors();
        const isLinkingExistingParticipant = linkedHouseholdParticipantsEnabled && !!member.linkExistingParticipant;

        if (householdMemberIsDeleted(member)) {
            return true;
        }

        if (isLinkingExistingParticipant && !member.linkedClientId) {
            isValid = false;
            member.errors.linkedParticipant.push('Select a participant to link');
        }

        if (!isLinkingExistingParticipant && !member.firstName) {
            isValid = false;
            member.errors.firstName.push('First Name is required');
        }

        if (!isLinkingExistingParticipant && !member.lastName) {
            isValid = false;
            member.errors.lastName.push('Last Name is required');
        }

        if (!isLinkingExistingParticipant && isHouseholdMemberDateOfBirthRequired(member) && !member.dateOfBirth) {
            isValid = false;
            member.errors.birthdate.push('Birthdate is required');
        }

        if (member.dateOfBirth && !isMatch(member.dateOfBirth, 'yyyy-MM-dd')) {
            isValid = false;
            member.errors.birthdate.push('Birthdate is not valid');
        }

        if (member.createParticipant && member.linkedClientId) {
            isValid = false;
            member.errors.createParticipant.push('Create as participant cannot be combined with a linked participant');
        }

        return isValid;
    };

    const validateHouseholdMemberAtIndex = (index: number) => {
        const member = model.householdMembers[index];

        if (!member) {
            return false;
        }

        const isValid = validateHouseholdMember(member);
        setModel({ ...model });
        return isValid;
    };

    const validate = () => {
        let isValid = true;

        const householdMembersToValidate = isHouseholdOnlyMode
            ? model.householdMembers.filter(member => !member.id || householdMemberIsUpdated(member) || householdMemberIsDeleted(member))
            : model.householdMembers;

        for (const member of model.householdMembers) {
            if (!householdMembersToValidate.includes(member)) {
                member.errors = getHouseholdMemberDefaultErrors();
            }
        }

        for (const member of householdMembersToValidate) {
            if (!validateHouseholdMember(member)) {
                isValid = false;
            }
        }

        model.errors = getParticipantDefaultErrors();
        if (isHouseholdOnlyMode) {
            setModel({ ...model });
            return isValid;
        }

        if (!model.firstName) {
            isValid = false;
            model.errors.firstName.push('First Name is required');
        }

        if (!model.lastName) {
            isValid = false;
            model.errors.lastName.push('Last Name is required');
        }

        if (dateOfBirthRequired && !model.dateOfBirth) {
            isValid = false;
            model.errors.birthdate.push('Birthdate is required');
        }

        if (model.activated) {
            if (!isMatch(model.activated, 'yyyy-MM-dd')) {
                isValid = false;
                model.errors.activated.push('Activated date is not valid');
            }
        } else {
            isValid = false;
            model.errors.activated.push('Activated Date is required');
        }

        if (model.selectedPrograms.length !== 0) {
            const selectedProgramEnrollmentRows = getSelectedProgramEnrollmentRows();

            if (selectedProgramEnrollmentRows.length === 0) {
                isValid = false;
                model.errors.enrolled.push('Select at least one participant to enroll');
            }

            for (const row of selectedProgramEnrollmentRows) {
                const selection = getProgramEnrollmentSelection(row.key);
                if (!selection.enrollmentDate) {
                    isValid = false;
                    model.errors.enrolled.push(`Enrollment date is required for ${row.label}`);
                } else if (!isMatch(selection.enrollmentDate, 'yyyy-MM-dd')) {
                    isValid = false;
                    model.errors.enrolled.push(`Enrollment date is not valid for ${row.label}`);
                }
            }

            if (model.enrolled && !isMatch(model.enrolled, 'yyyy-MM-dd')) {
                isValid = false;
                model.errors.enrolled.push('Default enrollment date is not valid');
            }
        }

        if (model.dateOfBirth && !isMatch(model.dateOfBirth, 'yyyy-MM-dd')) {
            isValid = false;
            model.errors.birthdate.push('Birthdate is not valid');
        }

        const profileFieldRequirements: Array<{
            fieldName: string;
            value: string | { label: string, value: string }[];
            label: string;
        }> = [
            { fieldName: 'race', value: model.raceIds, label: raceFieldLabel },
            { fieldName: 'ethnicity', value: model.ethnicityId, label: 'Ethnicity' },
            { fieldName: 'gender', value: model.genderId, label: 'Gender' },
            { fieldName: 'pronouns', value: model.pronouns, label: 'Preferred Pronouns' },
            { fieldName: 'address', value: model.addressLine1, label: 'Address' },
            { fieldName: 'city', value: model.addressCity, label: 'City' },
            { fieldName: 'state', value: model.addressState, label: 'State' },
            { fieldName: 'zip', value: model.addressZipCode, label: 'Zip Code' },
            { fieldName: 'phone', value: model.phoneNumber, label: 'Phone Number' },
            { fieldName: 'email', value: model.email, label: 'Email' }
        ];

        for (const field of profileFieldRequirements) {
            const isMissing = Array.isArray(field.value)
                ? field.value.length === 0
                : !field.value.trim();

            if (isProfileFieldRequired(field.fieldName) && isMissing) {
                isValid = false;
                model.errors[field.fieldName].push(`${field.label} is required`);
            }
        }

        setModel({ ...model });

        return isValid;
    };

    const buildHouseholdMemberRequest = (hhm: CreateOrEditHouseholdMemberModel) => {
        const householdMemberRaceSelection = splitRaceSelectionsForOrganization(hhm.raceIds || [], organization.value, racesOptions);

        return {
            id: hhm.id,
            firstName: hhm.firstName,
            middleName: hhm.middleName,
            lastName: hhm.lastName,
            dateOfBirth: hhm.dateOfBirth || null,
            ethnicityId: getRequestEthnicityId(householdMemberRaceSelection, hhm.ethnicityId),
            raceIds: householdMemberRaceSelection.raceIds,
            genderId: (hhm.genderId && Number(hhm.genderId)) || undefined,
            relationshipId: hhm.relationshipId ? Number(hhm.relationshipId) : null,
            phoneNumber: hhm.phoneNumber,
            email: hhm.email,
            linkedClientId: hhm.linkExistingParticipant ? (hhm.linkedClientId || undefined) : undefined,
            createParticipant: hhm.linkExistingParticipant
                ? undefined
                : !hhm.id && organization.value?.householdMemberParticipantMode === 'alwaysFullParticipant'
                    ? true
                    : !hhm.id && organization.value?.householdMemberParticipantMode === 'alwaysHouseholdMember'
                        ? undefined
                        : (hhm.createParticipant || undefined),
            householdId: model.householdId
        };
    };

    const handleUpdatingHousehold = async () => {
        try {
            const deletedHouseholdMemberIds = Array.from(new Set(model.deletedHouseholdMembers))
                .filter(deletedMemberId => model.householdMembers.some(hhm => hhm.id === deletedMemberId));
            const deletedHouseholdMemberIdSet = new Set(deletedHouseholdMemberIds);
            const activeHouseholdMembers = model.householdMembers
                .filter(hhm => !hhm.id || !deletedHouseholdMemberIdSet.has(hhm.id));
            const newHouseholdMembers = activeHouseholdMembers.filter(hhm => !hhm.id);
            const updatedHouseholdMembers = activeHouseholdMembers.filter(hhm => hhm.id && householdMemberIsUpdated(hhm));

            for (const householdMemberId of deletedHouseholdMemberIds) {
                await deleteHouseholdMember({
                    clientId: model.id,
                    householdMemberId
                });
            }

            for (const member of newHouseholdMembers) {
                await createHouseholdMember(model.id, buildHouseholdMemberRequest(member));
            }

            for (const member of updatedHouseholdMembers) {
                await updateHouseholdMember(model.id, member.id!, buildHouseholdMemberRequest(member));
            }

            toast.success('Successfully updated household');
            returnToEditOrigin();
        } catch (e) {
            logUnexpectedSubmitError(e);
            toast.error('Failed to update household');
            setSubmitError(e);
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdatingParticipant = async () => {
        try {
            const participantRaceSelection = splitRaceSelectionsForOrganization(model.raceIds || [], organization.value, racesOptions);
            const deletedHouseholdMemberIds = Array.from(new Set(model.deletedHouseholdMembers))
                .filter(deletedMemberId => model.householdMembers.some(hhm => hhm.id === deletedMemberId));
            const deletedHouseholdMemberIdSet = new Set(deletedHouseholdMemberIds);
            const activeHouseholdMembers = model.householdMembers
                .filter(hhm => !hhm.id || !deletedHouseholdMemberIdSet.has(hhm.id));

            await updateClient({
                id: model.id,
                firstName: model.firstName,
                middleName: model.middleName,
                lastName: model.lastName,
                dateOfBirth: model.dateOfBirth || null,
                genderId: (model.genderId && Number(model.genderId)) || undefined,
                ethnicityId: getRequestEthnicityId(participantRaceSelection, model.ethnicityId),
                raceIds: participantRaceSelection.raceIds,
                pronouns: model.pronouns,
                addressCity: model.addressCity,
                addressLine1: model.addressLine1,
                addressLine2: model.addressLine2,
                addressState: model.addressState,
                addressZipCode: model.addressZipCode,
                phoneNumber: model.phoneNumber,
                email: model.email,
                householdMembers: [
                    ...activeHouseholdMembers.map(hhm => {
                        return buildHouseholdMemberRequest(hhm);
                    })
                ],
                deletedHouseholdMembers: deletedHouseholdMemberIds,
                additionalInfoFields: fields && fields.length !== 0 ? fields.map(f => {
                    return {
                        instanceFieldId: f.instanceFieldId,
                        fieldId: f.id,
                        value: normalizeFormInstanceFieldValue(f, f.value),
                        files: f.files?.map(f => f.fileId!) || []
                    }
                }) : [],
                additionalInfoFormInstanceId: formInstance?.id,
                additionalInfoFormId: form?.id,
                additionalInfoFormVersionId: form?.versionId
            });

            toast.success(isHouseholdOnlyMode ? 'Successfully updated household' : 'Successfully updated participant');
            returnToEditOrigin();
        } catch (e) {
            logUnexpectedSubmitError(e);
            toast.error(isHouseholdOnlyMode ? 'Failed to update household' : 'Failed to update participant');
            setSubmitError(e);
        } finally {
            setSubmitting(false);
        }
    }

    const handleCreatingParticipant = async (createNew = false) => {
        setSubmittingAndCreateNew(createNew);

        try {
            const participantRaceSelection = splitRaceSelectionsForOrganization(model.raceIds || [], organization.value, racesOptions);
            const selectedProgramEnrollmentRows = getSelectedProgramEnrollmentRows();
            const primaryProgramEnrollmentSelection = getProgramEnrollmentSelection(primaryProgramEnrollmentKey);
            const enrollPrimaryParticipantInPrograms = model.selectedPrograms && model.selectedPrograms.length !== 0
                && selectedProgramEnrollmentRows.some(row => row.key === primaryProgramEnrollmentKey);
            const selectedHouseholdProgramEnrollmentRows = householdProgramEnrollmentEnabled
                ? selectedProgramEnrollmentRows.filter(row => row.householdMemberIndex !== undefined)
                : [];
            const deletedHouseholdMemberIds = new Set(model.deletedHouseholdMembers);
            const activeHouseholdMembers = model.householdMembers
                .filter(hhm => !hhm.id || !deletedHouseholdMemberIds.has(hhm.id));

            const client = await createClient({
                firstName: model.firstName,
                middleName: model.middleName,
                lastName: model.lastName,
                dateOfBirth: model.dateOfBirth || null,
                genderId: (model.genderId && Number(model.genderId)) || undefined,
                ethnicityId: getRequestEthnicityId(participantRaceSelection, model.ethnicityId),
                raceIds: participantRaceSelection.raceIds,
                pronouns: model.pronouns,
                addressCity: model.addressCity,
                addressLine1: model.addressLine1,
                addressLine2: model.addressLine2,
                addressState: model.addressState,
                addressZipCode: model.addressZipCode,
                phoneNumber: model.phoneNumber,
                email: model.email,
                activated: model.activated,
                publicFormInstanceId: props.publicFormInstanceId,
                householdMembers: [
                    ...activeHouseholdMembers.map(hhm => {
                        const request = buildHouseholdMemberRequest(hhm);
                        const { id, householdId, ...createRequest } = request;
                        return createRequest;
                    })
                ],
                additionalInfoFormId: fields && form?.fields.length !== 0 ? form?.id : undefined,
                additionalInfoFormVersionId: fields && form?.fields.length !== 0 ? form?.versionId : undefined,
                additionalInfoFields: fields && form?.fields.length !== 0 ? fields.map(f => {
                    return {
                        fieldId: f.id,
                        value: normalizeFormInstanceFieldValue(f, f.value),
                        files: f.files?.map(f => f.fileId!) || []
                    };
                }) : [],
                enrollInPrograms: enrollPrimaryParticipantInPrograms ? model.selectedPrograms.map(p => {
                    return {
                        programId: p.value,
                        enrollmentDate: primaryProgramEnrollmentSelection.enrollmentDate
                    }
                }) : [],
                householdMemberProgramEnrollments: selectedHouseholdProgramEnrollmentRows.flatMap(row => {
                    const selection = getProgramEnrollmentSelection(row.key);
                    return model.selectedPrograms.map(p => {
                        return {
                            householdMemberIndex: row.householdMemberIndex!,
                            programId: p.value,
                            enrollmentDate: selection.enrollmentDate
                        }
                    });
                })
            });

            if (client) {
                toast.success('Successfully created participant');

                if (createNew) {
                    if (props.onSave) {
                        props.onSave();
                    } else {
                        reset();
                    }
                } else {
                    try { localStorage.setItem('starterChecklist:lastClientId', client.id); } catch { }
                    navigate(`/participants/${client.id}/profile-demographics`);
                }
            } else {
                toast.error("Failed to create participant");
            }
        } catch (e) {
            logUnexpectedSubmitError(e);
            toast.error('Failed to create participant');
            setSubmitError(e);
        } finally {
            setSubmitting(false);
            setSubmittingAndCreateNew(false);
        }
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setSubmitError(undefined);
        if (!validate()) {
            toast.error("Validation error encountered");
            return;
        }
        setSubmitting(true);

        if (props.id && isHouseholdOnlyMode) {
            await handleUpdatingHousehold();
        } else if (props.id) {
            await handleUpdatingParticipant();
        } else {
            await handleCreatingParticipant();
        }
    };

    const handleCreating = async () => {
        setSubmitError(undefined);
        if (!validate()) {
            toast.error("Validation error encountered");
            return;
        }

        setSubmitting(true);

        await handleCreatingParticipant(true);
    }

    const resetHouseholdMemberLinkState = () => {
        setHouseholdMemberLinkQueries({});
        setHouseholdMemberLinkResults({});
        setHouseholdMemberLinkResultsVisible({});
        setHouseholdMemberLinkLoading({});
        setHouseholdMemberLinkError({});
    };

    const focusHouseholdMemberFirstField = (index: number) => {
        setTimeout(() => {
            const linkedSearchInput = document.getElementById(`household-member-linked-query-${index}`) as HTMLInputElement | null;
            const firstNameInput = document.getElementById(`household-member-first-name-${index}`) as HTMLInputElement | null;
            linkedSearchInput?.focus();
            if (!linkedSearchInput) {
                firstNameInput?.focus();
            }
        }, 0);
    };

    const handleAddingHouseholdMember = () => {
        const newIndex = model.householdMembers.length;
        const linkExistingParticipant = linkedHouseholdParticipantsEnabled && isHouseholdOnlyMode;
        const householdMemberParticipantMode = organization.value?.householdMemberParticipantMode || 'defaultUnchecked';

        setModel({
            ...model,
            householdMembers: [
                ...model.householdMembers,
                getDefaultHouseholdMember(linkExistingParticipant, householdMemberParticipantMode)
            ]
        });

        resetHouseholdMemberLinkState();
        setHouseholdMemberEditorOriginal(undefined);
        setHouseholdMemberEditorIndex(newIndex);
        focusHouseholdMemberFirstField(newIndex);
    };

    const handleEditingHouseholdMember = (index: number) => {
        const member = model.householdMembers[index];

        if (!member || householdMemberIsDeleted(member)) {
            return;
        }

        setHouseholdMemberEditorOriginal(cloneHouseholdMember(member));
        setHouseholdMemberEditorIndex(index);
        focusHouseholdMemberFirstField(index);
    };

    const handleRemoveHouseholdMember = (member: any) => {
        setModel(currentModel => {
            if (member.id) {
                return {
                    ...currentModel,
                    deletedHouseholdMembers: [
                        ...currentModel.deletedHouseholdMembers.filter(id => id !== member.id),
                        member.id
                    ]
                };
            }

            return {
                ...currentModel,
                householdMembers: currentModel.householdMembers.filter(hhm => hhm !== member)
            };
        });

        resetHouseholdMemberLinkState();

        if (householdMemberEditorIndex !== undefined) {
            setHouseholdMemberEditorIndex(undefined);
            setHouseholdMemberEditorOriginal(undefined);
        }
    };

    const handleRevertHouseholdMember = (member: CreateOrEditHouseholdMemberModel) => {
        if (!member.id) {
            handleRemoveHouseholdMember(member);
            return;
        }

        const originalMember = getOriginalHouseholdMember(member);
        if (!originalMember) {
            return;
        }

        setModel(currentModel => {
            return {
                ...currentModel,
                householdMembers: currentModel.householdMembers.map(hhm => hhm.id === member.id ? cloneHouseholdMember(originalMember) : hhm),
                deletedHouseholdMembers: currentModel.deletedHouseholdMembers.filter(id => id !== member.id)
            };
        });

        resetHouseholdMemberLinkState();
    };

    const handleCancelHouseholdMemberEditor = () => {
        if (householdMemberEditorIndex === undefined) {
            return;
        }

        const currentIndex = householdMemberEditorIndex;
        const currentMember = model.householdMembers[currentIndex];

        if (!householdMemberEditorOriginal) {
            setModel({
                ...model,
                householdMembers: model.householdMembers.filter((_, index) => index !== currentIndex)
            });
        } else if (currentMember) {
            setModel({
                ...model,
                householdMembers: model.householdMembers.map((hhm, index) => index === currentIndex ? cloneHouseholdMember(householdMemberEditorOriginal) : hhm)
            });
        }

        setHouseholdMemberEditorIndex(undefined);
        setHouseholdMemberEditorOriginal(undefined);
        resetHouseholdMemberLinkState();
    };

    const handleSaveHouseholdMemberEditor = () => {
        if (householdMemberEditorIndex === undefined) {
            return;
        }

        if (!validateHouseholdMemberAtIndex(householdMemberEditorIndex)) {
            toast.error("Validation error encountered");
            return;
        }

        setHouseholdMemberEditorIndex(undefined);
        setHouseholdMemberEditorOriginal(undefined);
        resetHouseholdMemberLinkState();
    };

    const handleChangingHouseholdMember = (updateField: (member: CreateOrEditHouseholdMemberModel) => void, index: number) => {
        setModel(currentModel => {
            return {
                ...currentModel,
                householdMembers: currentModel.householdMembers.map((member, memberIndex) => {
                    if (memberIndex !== index) {
                        return member;
                    }

                    const updatedMember = cloneHouseholdMember(member);
                    updateField(updatedMember);
                    return updatedMember;
                })
            };
        });
    };

    const clearHouseholdMemberLinkSearch = (index: number) => {
        setHouseholdMemberLinkQueries({
            ...householdMemberLinkQueries,
            [index]: ''
        });
        setHouseholdMemberLinkResults({
            ...householdMemberLinkResults,
            [index]: []
        });
        setHouseholdMemberLinkResultsVisible({
            ...householdMemberLinkResultsVisible,
            [index]: false
        });
        setHouseholdMemberLinkError({
            ...householdMemberLinkError,
            [index]: ''
        });
    };

    const handleLinkExistingParticipantChanged = (index: number, checked: boolean) => {
        handleChangingHouseholdMember(member => {
            member.linkExistingParticipant = checked;
            if (checked) {
                member.createParticipant = false;
            } else {
                member.linkedClientId = '';
            }
        }, index);

        clearHouseholdMemberLinkSearch(index);
    };

    const handleCreateParticipantChanged = (index: number, checked: boolean) => {
        handleChangingHouseholdMember(member => {
            member.createParticipant = checked;
            if (checked) {
                member.linkExistingParticipant = false;
                member.linkedClientId = '';
            }
        }, index);

        if (checked) {
            clearHouseholdMemberLinkSearch(index);
        }
    };

    const handleMakeHouseholdMemberHead = async (member: CreateOrEditHouseholdMemberModel) => {
        if (!props.id || !member.id || !member.linkedClientId) {
            return;
        }

        setSubmitError(undefined);
        setMakingHeadOfHouseholdMemberId(member.id);

        try {
            const result = await makeHouseholdMemberHeadOfHousehold({
                clientId: props.id,
                householdMemberId: member.id
            });

            toast.success('Head of household updated');
            navigate(`/households/${result.newHeadClientId}/edit`, {
                replace: true
            });
        } catch (e) {
            console.error(e);
            setSubmitError(e);
            toast.error('Failed to update head of household');
        } finally {
            setMakingHeadOfHouseholdMemberId('');
        }
    };

    const searchLinkedParticipantsForHouseholdMember = async (index: number) => {
        const rawQuery = (householdMemberLinkQueries[index] || '').trim();
        const query = rawQuery
            .replace(/,/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();

        setHouseholdMemberLinkLoading({
            ...householdMemberLinkLoading,
            [index]: true
        });

        setHouseholdMemberLinkResultsVisible({
            ...householdMemberLinkResultsVisible,
            [index]: false
        });

        setHouseholdMemberLinkError({
            ...householdMemberLinkError,
            [index]: ''
        });

        try {
            const result = await searchClients({
                query,
                skip: 0,
                take: 20,
                includeDeactivated: false
            });

            const displayParticipants = result.data.filter(p => {
                if (p.id === model.id) {
                    return false;
                }

                return true;
            });

            setLinkedParticipantsById({
                ...linkedParticipantsById,
                ...displayParticipants.reduce((previousValue: any, currentValue) => {
                    previousValue[currentValue.id] = currentValue;
                    return previousValue;
                }, {})
            });

            setHouseholdMemberLinkResults({
                ...householdMemberLinkResults,
                [index]: displayParticipants
            });

            setHouseholdMemberLinkResultsVisible({
                ...householdMemberLinkResultsVisible,
                [index]: true
            });
        } catch (e) {
            console.error(e);
            setHouseholdMemberLinkError({
                ...householdMemberLinkError,
                [index]: 'Failed to search participants'
            });
            setHouseholdMemberLinkResultsVisible({
                ...householdMemberLinkResultsVisible,
                [index]: false
            });
        } finally {
            setHouseholdMemberLinkLoading({
                ...householdMemberLinkLoading,
                [index]: false
            });
        }
    };

    const handleLinkedParticipantSelected = async (index: number, linkedClientId: string) => {
        if (!linkedClientId) {
            handleChangingHouseholdMember(member => {
                member.linkedClientId = '';
            }, index);
            setHouseholdMemberLinkError({
                ...householdMemberLinkError,
                [index]: ''
            });
            return;
        }

        if (linkedClientId === model.id) {
            setHouseholdMemberLinkError({
                ...householdMemberLinkError,
                [index]: 'Cannot link the same participant'
            });
            return;
        }

        const linkedElsewhere = model.householdMembers.some((hhm, memberIndex) => {
            return memberIndex !== index && hhm.linkedClientId === linkedClientId;
        });

        if (linkedElsewhere) {
            setHouseholdMemberLinkError({
                ...householdMemberLinkError,
                [index]: 'This participant is already linked in another household row'
            });
            return;
        }

        try {
            let linkedParticipant = linkedParticipantsById[linkedClientId];
            if (!linkedParticipant) {
                linkedParticipant = await getClientById({
                    id: linkedClientId
                });
                setLinkedParticipantsById({
                    ...linkedParticipantsById,
                    [linkedParticipant.id]: linkedParticipant
                });
            }

            setHouseholdMemberLinkError({
                ...householdMemberLinkError,
                [index]: ''
            });

            handleChangingHouseholdMember(member => {
                member.linkedClientId = linkedParticipant.id;
                member.linkExistingParticipant = true;
                member.createParticipant = false;
                member.firstName = linkedParticipant.firstName || '';
                member.middleName = linkedParticipant.middleName || '';
                member.lastName = linkedParticipant.lastName || '';
                member.dateOfBirth = formatJSONDateWithoutTime(linkedParticipant.dateOfBirth, 'yyyy-MM-dd');
                member.ethnicityId = linkedParticipant.ethnicityId?.toString() || '';
                member.genderId = getStoredGenderValueForOrganization({
                    genderId: linkedParticipant.genderId,
                    genderName: linkedParticipant.genderName,
                    genders: genderOptions
                }, organization.value);
                member.email = linkedParticipant.email || '';
                member.phoneNumber = linkedParticipant.phoneNumber || '';
                member.raceIds = getStoredRaceSelectionsForOrganization({
                    ethnicityId: linkedParticipant.ethnicityId,
                    ethnicityName: linkedParticipant.ethnicityName,
                    races: linkedParticipant.races
                }, organization.value);
            }, index);

            setHouseholdMemberLinkQueries({
                ...householdMemberLinkQueries,
                [index]: ''
            });

            setHouseholdMemberLinkResults({
                ...householdMemberLinkResults,
                [index]: []
            });
            setHouseholdMemberLinkResultsVisible({
                ...householdMemberLinkResultsVisible,
                [index]: false
            });
        } catch (e) {
            console.error(e);
            setHouseholdMemberLinkError({
                ...householdMemberLinkError,
                [index]: 'Failed to load selected participant'
            });
        }
    };

    const closeLinkedParticipantResults = (index: number) => {
        setHouseholdMemberLinkResultsVisible({
            ...householdMemberLinkResultsVisible,
            [index]: false
        });
    };

    const handleHouseholdMemberLinkQueryChanged = (index: number, value: string) => {
        setHouseholdMemberLinkQueries({
            ...householdMemberLinkQueries,
            [index]: value
        });
        setHouseholdMemberLinkResultsVisible({
            ...householdMemberLinkResultsVisible,
            [index]: false
        });
        setHouseholdMemberLinkError({
            ...householdMemberLinkError,
            [index]: ''
        });
    };

    const handleHouseholdMemberLinkQueryKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            searchLinkedParticipantsForHouseholdMember(index);
        }

        if (e.key === 'Escape') {
            e.preventDefault();
            closeLinkedParticipantResults(index);
        }
    };

    if (loading) {
        return (<div className="section">
            <Loader />
        </div>);
    }

    const printableHouseholdMembers = [];
    for (let i = 0; i < 1; i++) {
        printableHouseholdMembers.push(getDefaultHouseholdMember());
    }

    const renderLinkedParticipantSummary = ({
        idPrefix,
        clientId,
        firstName,
        middleName,
        lastName,
        dateOfBirth,
        genderId,
        email,
        phoneNumber,
        designation,
        children
    }: {
        idPrefix: string,
        clientId: string,
        firstName: string,
        middleName?: string,
        lastName: string,
        dateOfBirth?: string | null,
        genderId?: string,
        email?: string,
        phoneNumber?: string,
        designation?: string,
        children?: React.ReactNode
    }) => {
        const displayName = [firstName, middleName, lastName].filter(v => !!v).join(' ').trim() || 'Linked Participant';

        return (
            <div className="household-member-linked-summary">
                <div className="household-member-linked-summary-heading">
                    <div className="text-bold">{displayName}</div>
                    {designation && <span className="household-editor-designation-pill">{designation}</span>}
                </div>
                <div className="household-member-linked-summary-details">
                    {dateOfBirth && <span>{formatJSONDateWithoutTime(dateOfBirth, 'MM/dd/yyyy')}</span>}
                    {genderId && <span>{genderSelectOptions.find(g => g.value === genderId)?.label}</span>}
                    {email && <span>{email}</span>}
                    {phoneNumber && <span>{phoneNumber}</span>}
                </div>
                <div className="household-member-linked-summary-actions hide-on-print">
                    <Link id={`${idPrefix}-view-profile`} to={`/participants/${clientId}/profile-demographics`} target="_blank">View Profile</Link>
                    <Link id={`${idPrefix}-edit-profile`} to={`/participants/${clientId}/edit`} target="_blank">Edit Profile</Link>
                </div>
                {children}
            </div>
        );
    };

    const getHouseholdMember = (hhm: CreateOrEditHouseholdMemberModel, i: number, options?: { hideActions?: boolean }) => {
        const isLinkedParticipant = linkedHouseholdParticipantsEnabled && !!hhm.linkedClientId;
        const linkExistingParticipantEnabled = linkedHouseholdParticipantsEnabled && !!hhm.linkExistingParticipant;
        const lockLinkedParticipantFields = linkExistingParticipantEnabled;
        const useEditHouseholdMemberActions = householdEditorEnabled && isHouseholdOnlyMode;
        const removeHouseholdMemberText = useEditHouseholdMemberActions
            ? (isLinkedParticipant ? 'Remove from Household' : 'Delete Household Member')
            : 'Remove';
        const linkResults = householdMemberLinkResults[i];
        const showLinkResults = linkExistingParticipantEnabled && !!householdMemberLinkResultsVisible[i];
        const displayIndex = isHouseholdOnlyMode ? i + 2 : i + 1;
        const canCreateParticipantFromMember = linkedHouseholdParticipantsEnabled && !linkExistingParticipantEnabled;
        const householdMemberParticipantMode = organization.value?.householdMemberParticipantMode || 'defaultUnchecked';
        const createParticipantIsForced = !hhm.id
            && (householdMemberParticipantMode === 'alwaysHouseholdMember' || householdMemberParticipantMode === 'alwaysFullParticipant');
        const createParticipantMessages = hhm.errors?.createParticipant || [];

        return (<React.Fragment key={i}>
            <h3>Household Member {displayIndex}</h3>
            {linkedHouseholdParticipantsEnabled && <div className="row margin-vertical-2 hide-on-print">
                <div className="column">
                    <Checkbox id={`household-member-link-existing-participant-${i}`}
                        label="Add an Existing Participant"
                        containerClassName="unboxed household-member-compact-checkbox"
                        checked={linkExistingParticipantEnabled}
                        onChange={(checked) => handleLinkExistingParticipantChanged(i, checked)} />
                    <div className="household-member-option-help">Search for an existing participant and use their profile details for this household row.</div>
                    {hhm.errors?.linkedParticipant && hhm.errors.linkedParticipant.length > 0 && <ErrorMessages messages={hhm.errors.linkedParticipant} />}
                </div>
            </div>}
            {linkedHouseholdParticipantsEnabled && linkExistingParticipantEnabled && <div className="row margin-vertical-4 hide-on-print">
                <div className="column-large-9">
                    <Input id={`household-member-linked-query-${i}`}
                        label="Search Participants"
                        value={householdMemberLinkQueries[i] || ''}
                        onChange={e => handleHouseholdMemberLinkQueryChanged(i, e)}
                        onKeyDown={e => handleHouseholdMemberLinkQueryKeyDown(i, e)}
                        placeholder="Search by participant name" />
                </div>
                <div className="column-large-3">
                    <Button id={`household-member-linked-search-${i}`}
                        className="secondary margin-top-5"
                        text="Search"
                        loading={!!householdMemberLinkLoading[i]}
                        disabled={householdMemberLinkLoading[i]}
                        onClick={() => searchLinkedParticipantsForHouseholdMember(i)} />
                </div>
            </div>}
            {linkedHouseholdParticipantsEnabled && linkExistingParticipantEnabled && showLinkResults && <div className="row hide-on-print">
                <div className="column">
                    <div className="household-link-results-container">
                        <div className="household-link-floating-container">
                            <div className="household-link-results-header">
                                <div className="text-bold">Search Results</div>
                                <button
                                    id={`household-member-linked-close-results-${i}`}
                                    type="button"
                                    className="button flat small household-link-close-results-button"
                                    aria-label="Close search results"
                                    onClick={() => closeLinkedParticipantResults(i)}>
                                    <div className="button-contents">
                                        <i className="ri-close-line"></i>
                                    </div>
                                </button>
                            </div>
                            <div className="list-item-container">
                                {linkResults && linkResults.length === 0 && <div className="list-item">
                                    <div className="row justify-content-center">
                                        <div className="column-auto">No results found</div>
                                    </div>
                                </div>}
                                {linkResults && linkResults.length > 0 && linkResults.map(r => {
                                    return (<div className="list-item" key={r.id}>
                                        <div className="row justify-content-between align-items-center">
                                            <div className="column">
                                                <div className="row">
                                                    <div className="column-medium-6 column-7">
                                                        {r.firstName} {r.middleName} {r.lastName}
                                                    </div>
                                                    <div className="column-auto">
                                                        {r.dateOfBirth && formatJSONDateWithoutTime(r.dateOfBirth, 'MM/dd/yyyy')}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="column-auto">
                                                <Button id={`household-member-linked-select-${i}-${r.id}`}
                                                    text="Select"
                                                    className="secondary"
                                                    onClick={() => handleLinkedParticipantSelected(i, r.id)} />
                                            </div>
                                        </div>
                                    </div>);
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>}
            {linkedHouseholdParticipantsEnabled && linkExistingParticipantEnabled && (householdMemberLinkError[i] || householdMemberLinkLoading[i] || isLinkedParticipant) && <div className="row margin-vertical-2 align-items-center hide-on-print">
                {householdMemberLinkLoading[i] && <div className="column-auto">
                    <Loader noMarginTop={true} />
                </div>}
                {householdMemberLinkError[i] && <div className="column-auto">
                    <ErrorMessages messages={[householdMemberLinkError[i]]} />
                </div>}
            </div>}
            {linkExistingParticipantEnabled && <div className="row margin-vertical-4 align-items-start">
                <div className="column">
                    {isLinkedParticipant && renderLinkedParticipantSummary({
                        idPrefix: `household-member-linked-${i}`,
                        clientId: hhm.linkedClientId || '',
                        firstName: hhm.firstName,
                        middleName: hhm.middleName,
                        lastName: hhm.lastName,
                        dateOfBirth: hhm.dateOfBirth,
                        genderId: hhm.genderId,
                        email: hhm.email,
                        phoneNumber: hhm.phoneNumber,
                        children: <>
                        <div className="household-member-linked-relationship">
                            <ValidatedSelect id={`household-member-relationship-${i}`}
                                onChange={(e) => handleChangingHouseholdMember(member => member.relationshipId = e, i)}
                                value={hhm.relationshipId}
                                label="Relationship to Participant"
                                messages={hhm.errors?.relationship || []}>
                                <option value="">---Select---</option>
                                {relationshipOptions.map(r => {
                                    return <option key={r.id} value={r.id}>{r.name}</option>
                                })}
                            </ValidatedSelect>
                        </div>
                        {useEditHouseholdMemberActions && hhm.id && <div className="household-member-linked-summary-actions hide-on-print">
                            <Button id={`household-member-make-head-${i}`}
                                text="Make Head of Household"
                                className="secondary small"
                                loading={makingHeadOfHouseholdMemberId === hhm.id}
                                disabled={!!makingHeadOfHouseholdMemberId || submitting}
                                onClick={() => handleMakeHouseholdMemberHead(hhm)} />
                        </div>}
                        </>
                    })}
                </div>
            </div>}
            {!linkExistingParticipantEnabled && <>
            <div className="row margin-vertical-4">
                <div className={getColumnClass()}>
                    <ValidatedInput id={`household-member-first-name-${i}`}
                        onChange={(e) => handleChangingHouseholdMember(member => member.firstName = e, i)}
                        readOnly={lockLinkedParticipantFields}
                        required={!linkExistingParticipantEnabled}
                        value={hhm.firstName}
                        label="First Name"
                        messages={hhm.errors?.firstName || []} />

                </div>
                <div className={getColumnClass()}>
                    <Input id={`household-member-middle-name-${i}`}
                        onChange={(e) => handleChangingHouseholdMember(member => member.middleName = e, i)}
                        readOnly={lockLinkedParticipantFields}
                        value={hhm.middleName}
                        label="Middle Name" />

                </div>
                <div className={getColumnClass()}>
                    <ValidatedInput id={`household-member-last-name-${i}`}
                        onChange={(e) => handleChangingHouseholdMember(member => member.lastName = e, i)}
                        readOnly={lockLinkedParticipantFields}
                        required={!linkExistingParticipantEnabled}
                        value={hhm.lastName}
                        label="Last Name"
                        messages={hhm.errors?.lastName || []} />

                </div>
                <div className={getColumnClass()}>
                    <ValidatedInput id={`household-member-birth-date-${i}`}
                        onChange={(e) => handleChangingHouseholdMember(member => member.dateOfBirth = e, i)}
                        readOnly={lockLinkedParticipantFields}
                        required={!linkExistingParticipantEnabled && isHouseholdMemberDateOfBirthRequired(hhm)}
                        value={hhm.dateOfBirth}
                        label="Date of Birth"
                        type="date"
                        messages={hhm.errors?.birthdate || []} />
                    {canCreateParticipantFromMember && !createParticipantIsForced && <div className="hide-on-print household-member-create-participant-inline">
                        <Checkbox id={`household-member-create-participant-${i}`}
                            label="Create as participant"
                            containerClassName="unboxed household-member-compact-checkbox"
                            checked={!!hhm.createParticipant}
                            onChange={(checked) => handleCreateParticipantChanged(i, checked)} />
                        <div className="household-member-option-help">Create a full participant profile from this household member when saving.</div>
                        {createParticipantMessages.length > 0 && <ErrorMessages messages={createParticipantMessages} />}
                    </div>}
                    {canCreateParticipantFromMember && createParticipantIsForced && <div className="hide-on-print household-member-option-help">
                        {householdMemberParticipantMode === 'alwaysFullParticipant'
                            ? 'This household member will be created as a full participant when saving.'
                            : 'This household member will be kept as a household member when saving.'}
                    </div>}
                </div>
            </div>
            <div className="row margin-vertical-4">
                <div className="column-large-6">
                    <Input id="phone-number"
                        onChange={(e) => handleChangingHouseholdMember(member => member.phoneNumber = e, i)}
                        readOnly={lockLinkedParticipantFields}
                        value={hhm.phoneNumber}
                        label="Phone Number"
                        mask="(000)000-0000"
                        useMaskedValue={true} />
                </div>
                <div className="column-large-6">
                    <Input id="email"
                        onChange={(e) => handleChangingHouseholdMember(member => member.email = e, i)}
                        readOnly={lockLinkedParticipantFields}
                        value={hhm.email}
                        label="Email" />
                </div>
            </div>
            <div className="row margin-vertical-4">
                <div className={getColumnClass()}>
                    <ValidatedSelect id={`household-member-relationship-${i}`}
                        onChange={(e) => handleChangingHouseholdMember(member => member.relationshipId = e, i)}
                        value={hhm.relationshipId}
                        label="Relationship to Participant"
                        messages={hhm.errors?.relationship || []}>
                        <option value="">---Select---</option>
                        {relationshipOptions.map(r => {
                            return <option key={r.id} value={r.id}>{r.name}</option>
                        })}
                    </ValidatedSelect>
                </div>
                {showGender && <div className={getColumnClass()}>
                    <Select id={`household-member-gender-${i}`}
                        onChange={(e) => handleChangingHouseholdMember(member => member.genderId = e, i)}
                        disabled={lockLinkedParticipantFields}
                        value={hhm.genderId} label="Gender">
                        <option value="">--Select--</option>
                        {genderSelectOptions.map(go => {
                            return <option key={go.value} value={go.value}>{go.label}</option>
                        })}
                    </Select>
                </div>}
                {showEthnicity && <div className={getColumnClass()}>
                    <RadioButton id={`household-member-ethnicity-${i}`}
                        onChange={(e) => handleChangingHouseholdMember(member => member.ethnicityId = e, i)}
                        disabled={lockLinkedParticipantFields}
                        value={hhm.ethnicityId}
                        label="Ethnicity"
                        options={ethnicityOptions.map(e => {
                            return {
                                label: e.name,
                                value: e.id.toString()
                            }
                        })} />
                </div>}
            </div>
            {showRace && <div className="row margin-vertical-4">
                <div className="column">
                    <CheckboxGroup id={`household-member-race-${i}`}
                        onChange={(e) => handleChangingHouseholdMember(member => member.raceIds = e, i)}
                        disabled={lockLinkedParticipantFields}
                        checkedOptions={hhm.raceIds} label={raceFieldLabel} options={raceSelectOptions.map(ro => {
                            return {
                                label: ro.label,
                                value: ro.value
                            }
                        })} />
                </div>
            </div>}</>}
            {!options?.hideActions && <div className="row margin-vertical-4 justify-content-end hide-on-print">
                <div className={getclassNames('column-auto', useEditHouseholdMemberActions && isLinkedParticipant ? 'household-member-remove-action-column' : '')}>
                    <Button id={`remove-household-member-${i}`}
                        text={removeHouseholdMemberText}
                        onClick={() => handleRemoveHouseholdMember(hhm)} />
                </div>
            </div>}
            {!options?.hideActions && useEditHouseholdMemberActions && isLinkedParticipant && <div className="row justify-content-end hide-on-print">
                <div className="column-auto">
                    <div className="warning-message">
                        Removing a participant from the household does not delete the participant
                    </div>
                </div>
            </div>}
            {i !== model.householdMembers.length - 1 && <hr className="margin-vertical-5" />}
        </React.Fragment>)
    }

    const goToPossibleDuplicates = () => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth'
        });
        setShowingPossibleDuplicates(true);
        setHighlightDuplicateBackgrounds(true);
        setTimeout(() => {
            setHighlightDuplicateBackgrounds(false);
        }, 3000);
    }

    const getColumnClass = () => {
        if (props.columnNumber) {
            return `column-large-${Math.trunc(12 / props.columnNumber)}`
        }

        return 'column-large-4';
    };

    const linkedHouseholdOwnerDisplayNames = linkedHouseholdOwners.map(owner => {
        const fullName = [owner.firstName, owner.middleName, owner.lastName].filter(v => !!v).join(' ').trim();
        return fullName || 'Linked Participant';
    });
    const hasOwnedHousehold = householdIdentifiersEnabled && model.householdId !== undefined && model.householdId !== null;

    const getHouseholdOwner = () => {
        const householdOwnerName = `${model.firstName || ''} ${model.lastName || ''}`.trim() || 'this household member';

        return (<React.Fragment>
            <h3>Household Member 1</h3>
            <div className="row margin-vertical-4 align-items-start">
                <div className="column">
                    {renderLinkedParticipantSummary({
                        idPrefix: 'household-owner-linked',
                        clientId: model.id || props.id || '',
                        firstName: model.firstName,
                        middleName: model.middleName,
                        lastName: model.lastName,
                        dateOfBirth: model.dateOfBirth,
                        genderId: model.genderId,
                        email: model.email,
                        phoneNumber: model.phoneNumber,
                        designation: 'Head of Household'
                    })}
                </div>
            </div>
            <div className="row margin-top-2 margin-bottom-4 hide-on-print">
                <div className="column">
                    <div className="warning-message">
                        To remove {householdOwnerName}, first change the head of household.
                    </div>
                </div>
            </div>
            {model.householdMembers.length > 0 && <hr className="margin-vertical-5" />}
        </React.Fragment>);
    };

    const getHouseholdMemberDisplayName = (member: CreateOrEditHouseholdMemberModel) => {
        return [member.firstName, member.middleName, member.lastName].filter(v => !!v).join(' ').trim() || 'Unnamed Member';
    };

    const getHouseholdMemberRelationshipName = (member: CreateOrEditHouseholdMemberModel) => {
        return relationshipOptions.find(r => r.id.toString() === member.relationshipId)?.name || '';
    };

    const getHouseholdMemberStatusClass = (member: CreateOrEditHouseholdMemberModel) => {
        return getHouseholdMemberChangeStatus(member).toLowerCase();
    };

    const renderHouseholdMembersTable = (includeOwner: boolean) => {
        const activeHouseholdMembers = model.householdMembers;
        const hasRows = includeOwner || activeHouseholdMembers.length !== 0;

        return (
            <div className="household-members-table-container hide-on-print">
                {!hasRows && <div className="household-members-empty">No household members have been added.</div>}
                {hasRows && <table id="household-members-table" className="table household-members-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Relationship</th>
                            <th>Birthdate</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {includeOwner && <tr>
                            <td>
                                <small>{[model.firstName, model.middleName, model.lastName].filter(v => !!v).join(' ').trim() || 'Head of Household'}</small>
                                <span className="household-editor-designation-pill margin-left-2">Head of Household</span>
                            </td>
                            <td><small>Head of Household</small></td>
                            <td><small>{model.dateOfBirth && formatJSONDateWithoutTime(model.dateOfBirth, 'MM/dd/yyyy')}</small></td>
                            <td></td>
                            <td>
                                <small>Change the head of household before removing this member.</small>
                            </td>
                        </tr>}
                        {activeHouseholdMembers.map((member, index) => {
                            const status = getHouseholdMemberChangeStatus(member);
                            const canRevert = status === 'Modified' || status === 'Removed';
                            const isDeleted = status === 'Removed';
                            const options = [
                                ...(!isDeleted ? [{
                                    label: 'Edit',
                                    onClick: () => handleEditingHouseholdMember(index)
                                }] : []),
                                ...(!isDeleted ? [{
                                    label: member.id ? 'Delete' : 'Remove',
                                    onClick: () => handleRemoveHouseholdMember(member)
                                }] : []),
                                ...(canRevert ? [{
                                    label: 'Revert',
                                    onClick: () => handleRevertHouseholdMember(member)
                                }] : [])
                            ];

                            return (
                                <tr key={member.id || `new-${index}`} className={getclassNames('household-member-table-row', isDeleted ? 'pending-delete' : '')}>
                                    <td>
                                        <small>{getHouseholdMemberDisplayName(member)}</small>
                                        {member.linkedClientId && <span className="household-editor-designation-pill margin-left-2">Linked Participant</span>}
                                    </td>
                                    <td><small>{getHouseholdMemberRelationshipName(member) || '-'}</small></td>
                                    <td><small>{member.dateOfBirth && formatJSONDateWithoutTime(member.dateOfBirth, 'MM/dd/yyyy')}</small></td>
                                    <td>{status && <span className={`household-member-status ${getHouseholdMemberStatusClass(member)}`}>{status}</span>}</td>
                                    <td>
                                        <div className="household-member-table-actions">
                                            <Dropdown id={`household-member-actions-${index}`}
                                                options={options}
                                                optionSize="small"
                                                alignRight={true} />
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>}
            </div>
        );
    };

    const renderHouseholdMemberEditorModal = () => {
        const editingMember = householdMemberEditorIndex !== undefined
            ? model.householdMembers[householdMemberEditorIndex]
            : undefined;

        return (
            <Modal id="household-member-editor-modal"
                visible={householdMemberEditorIndex !== undefined && !!editingMember}
                className="medium household-member-editor-modal"
                title={householdMemberEditorOriginal ? 'Edit Household Member' : 'Add Household Member'}
                onClose={handleCancelHouseholdMemberEditor}>
                {editingMember && householdMemberEditorIndex !== undefined && <>
                    {getHouseholdMember(editingMember, householdMemberEditorIndex, { hideActions: true })}
                    <div className="row justify-content-between margin-top-5 hide-on-print align-items-center">
                        <div className="column-auto">
                            <Button id="cancel-household-member-editor"
                                text="Cancel"
                                className="flat"
                                onClick={handleCancelHouseholdMemberEditor} />
                        </div>
                        <div className="column-auto">
                            <Button id="save-household-member-editor"
                                text="Save Member"
                                onClick={handleSaveHouseholdMemberEditor} />
                        </div>
                    </div>
                </>}
            </Modal>
        );
    };

    if (isHouseholdOnlyMode) {
        return (
            <div className="section">
                <div className="row justify-content-center">
                    <div className="column">
                        {!props.isEmbedded && <div className="row align-items-center justify-content-between hide-on-print">
                            <div className="column-auto">
                                <BackButton useReturnLocation={useReturnLocation} />
                            </div>
                        </div>}
                        <h1 className="form-header">Edit Household</h1>
                        <form onSubmit={handleSubmit} className="participant-form">
                            <ApiErrorMessages error={submitError} />
                            <div className="row margin-top-5 align-items-center justify-content-between hide-on-print household-member-sticky-toolbar">
                                <div className="column-auto">
                                    <h2 className="section-header">Household Members</h2>
                                </div>
                                <div className="column-auto">
                                    <Button id="add-household-member"
                                        onClick={handleAddingHouseholdMember}
                                        text="Add Member" />
                                </div>
                            </div>
                            <div className="show-on-print">
                                <h2 className="section-header">Household Members</h2>
                            </div>
                            {renderHouseholdMembersTable(true)}
                            <div className="show-on-print">
                                {getHouseholdOwner()}
                                {model.householdMembers.map((hhm, i) => {
                                    return getHouseholdMember(hhm, i);
                                })}
                            </div>
                            {renderHouseholdMemberEditorModal()}
                            <div className="row justify-content-between margin-top-5 hide-on-print align-items-center">
                                <div className="column-auto">
                                    <Button id="cancel-edit-household-button"
                                        text="Cancel"
                                        className="flat"
                                        onClick={returnToEditOrigin} />
                                </div>
                                <div className="column-auto">
                                    <Button id="save-household-button"
                                        type="submit"
                                        text="Save Household"
                                        loading={submitting}
                                        disabled={submitting} />
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="section">
            <div className="row justify-content-center">
                <div className="column">
                    {!props.isEmbedded && <div className="row align-items-center justify-content-between hide-on-print">
                        <div className="column-auto">
                            <BackButton useReturnLocation={useReturnLocation} />
                        </div>
                        <div className="column-auto">
                            <Button id="print-button"
                                text="Print"
                                className="secondary small"
                                onClick={handleOnPrint} />
                        </div>
                    </div>}
                    <h1 className="form-header">{props.id ? 'Edit' : 'Add New'} Participant</h1>
                    <form onSubmit={handleSubmit} className="participant-form">
                        <ApiErrorMessages error={submitError} />
                        <h2 className="section-header">Basic Information</h2>
                        {!model.id && <div className="row margin-vertical-4">
                            <div className={`${getColumnClass()} hide-on-print`}>
                                <ValidatedInput id="activated-date"
                                    onChange={(e) => setModel({ ...model, activated: e })}
                                    value={model.activated}
                                    label="Activated Date"
                                    type="date"
                                    required={true}
                                    messages={model.errors?.activated || []} />
                            </div>
                            <div className={`${getColumnClass()} show-on-print`}>
                                <Input id="activated-date"
                                    value=""
                                    readOnly={true}
                                    label="Activated Date"
                                    type="text"
                                    required={true} />
                            </div>
                        </div>}
                        <div className="row margin-vertical-4">
                            <div className={getColumnClass()}>
                                <ValidatedInput id="first-name"
                                    onChange={(e) => setModel({ ...model, firstName: e })}
                                    required={true} value={model.firstName}
                                    ref={firstNameRef}
                                    label="First Name"
                                    messages={model.errors?.firstName || []} />
                            </div>
                            <div className={getColumnClass()}>
                                <Input id="middle-name"
                                    onChange={(e) => setModel({ ...model, middleName: e })}
                                    value={model.middleName}
                                    label="Middle Name" />
                            </div>
                            <div className={getColumnClass()}>
                                <ValidatedInput id="last-name"
                                    onChange={(e) => setModel({ ...model, lastName: e })}
                                    required={true}
                                    value={model.lastName}
                                    label="Last Name"
                                    messages={model.errors?.lastName || []} />
                            </div>
                            <div className={getColumnClass()}>
                                <ValidatedInput id="birth-date"
                                    onChange={(e) => setModel({ ...model, dateOfBirth: e })}
                                    required={dateOfBirthRequired}
                                    value={model.dateOfBirth}
                                    label="Date of Birth"
                                    type="date"
                                    messages={model.errors?.birthdate || []} />
                            </div>
                        </div>
                        {!props.id && <>
                            <div className="row align-items-center padding-vertical-2">
                                <div className={getclassNames('column-auto', possibleDuplicates.length !== 0 ? 'text-danger' : '')}>
                                    Possible Duplicates Found: {possibleDuplicates.length}
                                </div>
                                {checkingDuplicate && <div className="column-auto">
                                    <Loader noMarginTop={true} />
                                </div>}
                                {possibleDuplicates.length !== 0 && <div className="column-auto" aria-live="polite">
                                    <Button id="show-possible-duplicates"
                                        className="secondary"
                                        onClick={() => setShowingPossibleDuplicates(!showingPossibleDuplicates)}
                                        text={showingPossibleDuplicates ? 'Hide Possible Duplicates' : 'Show Possible Duplicates'}
                                        icon={showingPossibleDuplicates ? (<i className="ri-arrow-up-s-line" />) : (<i className="ri-arrow-down-s-line" />)}
                                        iconLocation="end" />

                                </div>}
                            </div>
                            {showingPossibleDuplicates && possibleDuplicates && possibleDuplicates.length !== 0 && <div className="row margin-vertical-4">
                                <div className="column hide-on-print">
                                    <div className="list-item-container">
                                        {possibleDuplicates.map(d => {
                                            return (<div className={getclassNames('list-item', highlightDuplicateBackgrounds ? 'highlight' : '')} key={d.id}>
                                                <div className="row justify-content-between align-items-center">
                                                    <div className="column-6">
                                                        <div className="row justify-content-between align-items-center">
                                                            <div className="column-auto">
                                                                <Link to={`/participants/${d.id}/profile-demographics`} target="_blank">{d.lastName}, {d.firstName}</Link>
                                                            </div>
                                                            <div className="column-auto">
                                                                <small>
                                                                    <div>Birth Date - {formatJSONDateWithoutTime(d.birthDate, 'MM/dd/yyyy')}</div>
                                                                    {d.phoneNumber && <div>Phone Number - {d.phoneNumber}</div>}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="column-auto">
                                                        <small>
                                                            <div>Created - {format(new Date(d.created), 'MM/dd/yyyy')}</div>
                                                            {d.updated && <div>Updated - {format(new Date(d.updated), 'MM/dd/yyyy')}</div>}
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>)
                                        })}
                                    </div>
                                </div>
                            </div>}
                        </>}

                        <div className="row margin-vertical-4">
                            {showPronouns && <div className={getColumnClass()}>
                                <ValidatedInput id="preferred-pronouns"
                                    onChange={(e) => setModel({ ...model, pronouns: e })}
                                    value={model.pronouns}
                                    label="Preferred Pronouns"
                                    required={isProfileFieldRequired('pronouns')}
                                    messages={model.errors?.pronouns || []} />
                            </div>}
                            {showGender && <div className={getColumnClass()}>
                                <ValidatedSelect id="gender"
                                    onChange={(e) => setModel({ ...model, genderId: e })}
                                    value={model.genderId}
                                    label="Gender"
                                    required={isProfileFieldRequired('gender')}
                                    messages={model.errors?.gender || []}>
                                    <option value="">--Select--</option>
                                    {genderSelectOptions.map(go => {
                                        return <option key={go.value} value={go.value}>{go.label}</option>
                                    })}
                                </ValidatedSelect>
                            </div>}
                            {showEthnicity && <div className={getColumnClass()}>
                                <RadioButton id="ethnicity"
                                    onChange={(e) => setModel({ ...model, ethnicityId: e })}
                                    value={model.ethnicityId}
                                    label="Ethnicity"
                                    required={isProfileFieldRequired('ethnicity')}
                                    options={ethnicityOptions.map(e => {
                                        return {
                                            label: e.name,
                                            value: e.id.toString()
                                        }
                                    })} />
                                <ErrorMessages messages={model.errors?.ethnicity || []} />
                            </div>}
                        </div>
                        {showRace && <div className="row margin-vertical-4">
                            <div className="column">
                                <CheckboxGroup id="race"
                                    onChange={(e) => setModel({ ...model, raceIds: e })}
                                    checkedOptions={model.raceIds}
                                    label={raceFieldLabel}
                                    required={isProfileFieldRequired('race')}
                                    options={raceSelectOptions.map(ro => {
                                        return {
                                            label: ro.label,
                                            value: ro.value
                                        }
                                    })} />
                                <ErrorMessages messages={model.errors?.race || []} />
                            </div>
                        </div>}
                        {(showPhone || showEmail) && <div className="row margin-vertical-4">
                            {showPhone && <div className="column-large-6">
                                <ValidatedInput id="phone-number"
                                    onChange={(e) => setModel({ ...model, phoneNumber: e })}
                                    value={model.phoneNumber}
                                    label="Phone Number"
                                    required={isProfileFieldRequired('phone')}
                                    mask="(000)000-0000"
                                    useMaskedValue={true}
                                    messages={model.errors?.phone || []} />
                            </div>}
                            {showEmail && <div className="column-large-6">
                                <ValidatedInput id="email"
                                    onChange={(e) => setModel({ ...model, email: e })}
                                    value={model.email}
                                    label="Email"
                                    required={isProfileFieldRequired('email')}
                                    messages={model.errors?.email || []} />
                            </div>}
                        </div>}
                        {(showAddress || showCity || showState || showZip) && <h2 className="section-header">Address</h2>}
                        {showAddress && <><div className="row margin-vertical-4">
                            <div className="column">
                                <ValidatedInput id="address-line-1"
                                    onChange={(e) => setModel({ ...model, addressLine1: e })}
                                    value={model.addressLine1}
                                    label="Line 1"
                                    required={isProfileFieldRequired('address')}
                                    messages={model.errors?.address || []} />
                            </div>
                        </div>
                        <div className="row margin-vertical-4">
                            <div className="column">
                                <Input id="address-line-2"
                                    onChange={(e) => setModel({ ...model, addressLine2: e })}
                                    value={model.addressLine2}
                                    label="Line 2" />
                            </div>
                        </div></>}
                        {(showCity || showState || showZip) && <div className="row margin-vertical-4">
                            {showCity && <div className={getColumnClass()}>
                                <ValidatedInput id="city"
                                    onChange={(e) => setModel({ ...model, addressCity: e })}
                                    value={model.addressCity}
                                    label="City"
                                    required={isProfileFieldRequired('city')}
                                    messages={model.errors?.city || []} />
                            </div>}
                            {showState && <div className={getColumnClass()}>
                                <span className="hide-on-print">
                                    <ValidatedSelect id="state"
                                        onChange={(e) => setModel({ ...model, addressState: e })}
                                        value={model.addressState}
                                        label="State"
                                        required={isProfileFieldRequired('state')}
                                        messages={model.errors?.state || []}>
                                        <option>Select One</option>
                                        {states.map(s => {
                                            return <option key={s.value} value={s.value}>{s.label}</option>
                                        })}
                                    </ValidatedSelect>
                                </span>
                                <span className="show-on-print">
                                    <Input id="state"
                                        onChange={(e) => setModel({ ...model, addressState: e })}
                                        value={model.addressState}
                                        label="State"
                                        required={isProfileFieldRequired('state')} />
                                </span>
                            </div>}
                            {showZip && <div className={getColumnClass()}>
                                <ValidatedInput id="zip-code"
                                    onChange={(e) => setModel({ ...model, addressZipCode: e })}
                                    value={model.addressZipCode}
                                    label="Zip Code"
                                    required={isProfileFieldRequired('zip')}
                                    messages={model.errors?.zip || []} />
                            </div>}
                        </div>}
                        {!!props.id && !isHouseholdOnlyMode && householdEditorEnabled && linkedHouseholdParticipantsEnabled && <div className="row margin-vertical-4 hide-on-print">
                            <div className="column">
                                <h2 className="section-header">Household and Family Members</h2>
                                {model.householdMembers.length === 0 && !hasOwnedHousehold &&
                                    <Button id="open-edit-household-button"
                                        text={`Start ${model.firstName} ${model.lastName} Household`}
                                        className="secondary small"
                                        href={`/households/${model.id || props.id}/edit`}
                                        newWindow={true} />}
                                {(model.householdMembers.length !== 0 || hasOwnedHousehold) && <div className="row">
                                    {linkedHouseholdParticipantsEnabled &&
                                        <div className="column-large-6">
                                            <div className="card">
                                                <div className="card-container small">
                                                    {householdIdentifiersEnabled && hasOwnedHousehold && <div className="margin-bottom-2">
                                                        <small>Household ID: {model.householdId}</small>
                                                    </div>}
                                                    <Button id="open-edit-household-button"
                                                        text={`Edit ${model.firstName} ${model.lastName} Household (${model.householdMembers.length + 1})`}
                                                        className="secondary small"
                                                        href={`/households/${model.id || props.id}/edit`}
                                                        newWindow={true} />
                                                    <table className="table margin-top-5">
                                                        <thead>
                                                            <tr>
                                                                <th>name</th>
                                                                <th>birthdate</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr>
                                                                <td><small>{model.firstName} {model.lastName}</small></td>
                                                                <td><small>{formatJSONDateWithoutTime(model.dateOfBirth, 'MM/dd/yyyy')}</small></td>
                                                            </tr>
                                                            {model.householdMembers.map(m => {
                                                                return <tr key={m.id}>
                                                                    <td>
                                                                        <small>{m.firstName} {m.lastName}</small>
                                                                    </td>
                                                                    <td>
                                                                        <small>{formatJSONDateWithoutTime(m.dateOfBirth, 'MM/dd/yyyy')}</small>
                                                                    </td>
                                                                </tr>
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>}
                                </div>}
                                {linkedHouseholdOwners.length !== 0 && <div className="row margin-bottom-2">
                                    <div className="column-auto">
                                        <h3>{model.firstName} {model.lastName} is also linked to the following households</h3>
                                    </div>
                                </div>}
                                {linkedHouseholdOwners.length !== 0 && <div>
                                    <div className="row">
                                        {linkedHouseholdParticipantsEnabled && linkedHouseholdOwners.map((owner, index) => <div key={owner.id} className="column-large-6">
                                            <div className="card">
                                                <div className="card-container small">
                                                    {householdIdentifiersEnabled && owner.householdId !== undefined && owner.householdId !== null && <div className="margin-bottom-2">
                                                        <small>Household ID: {owner.householdId}</small>
                                                    </div>}
                                                    <Button id={`open-linked-edit-household-button-${index}`}
                                                        text={`Edit ${linkedHouseholdOwnerDisplayNames[index]} Household (${owner.members.length + 1})`}
                                                        className="secondary small"
                                                        href={`/households/${owner.id}/edit`}
                                                        newWindow={true} />
                                                    <table className="table margin-top-5">
                                                        <thead>
                                                            <tr>
                                                                <th>name</th>
                                                                <th>birthdate</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr>
                                                                <td><small>{owner.firstName} {owner.lastName}</small></td>
                                                                <td><small>{formatJSONDateWithoutTime(owner.birthDate, 'MM/dd/yyyy')}</small></td>
                                                            </tr>
                                                            {owner.members.map(m => {
                                                                return <tr key={m.id}>
                                                                    <td>
                                                                        <small>{m.firstName} {m.lastName}</small>
                                                                    </td>
                                                                    <td>
                                                                        <small>{formatJSONDateWithoutTime(m.dateOfBirth, 'MM/dd/yyyy')}</small>
                                                                    </td>
                                                                </tr>
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                        )}
                                    </div>
                                </div>}
                            </div>
                        </div>}
                        {showHouseholdEditingSection && <>
                            <div className="hide-on-print household-member-sticky-toolbar">
                                <h2 className="section-header">Household and Family Members</h2>
                                <Button id="add-household-member"
                                    onClick={handleAddingHouseholdMember}
                                    text="Add Member" />
                            </div>
                            {renderHouseholdMembersTable(false)}
                            {renderHouseholdMemberEditorModal()}
                        </>}
                        {fields && fields.length > 0 && (<>
                            <h2 className="section-header">Additional Information</h2>
                            <FormEditor disabled={false}
                                fields={fields}
                                onChange={handleOnFieldsChange}
                                columnNumber={props.columnNumber} />
                        </>)}
                        {!model.id && <h2>Enroll in Programs</h2>}
                        {!model.id && programs.length !== 0 && <div className="row margin-vertical-4 align-items-center hide-on-print">
                            <div className="column-auto">
                                <Button id="assign-to-programs"
                                    text={showPrograms ? 'Hide Programs' : 'Show Programs'}
                                    icon={showPrograms ? (<i className="ri-arrow-up-s-line" />) : (<i className="ri-arrow-down-s-line" />)}
                                    iconLocation="end"
                                    className="secondary"
                                    onClick={() => setShowPrograms(!showPrograms)} />
                            </div>
                            <div className="column-auto">
                                Selected Programs: {model.selectedPrograms.length}
                            </div>
                            {model.errors && model.errors.enrolled.length !== 0 && model.selectedPrograms.length !== 0 && !showPrograms && <div className="column-auto">
                                <ErrorMessages messages={model.errors.enrolled} />
                            </div>}
                        </div>}
                        {programs.length !== 0 && <div className="row margin-vertical-4 show-on-print">
                            <div className="column">
                                <CheckboxGroup id="available-programs"
                                    label="Programs"
                                    options={programs.map(p => {
                                        return {
                                            label: p.name,
                                            value: p.id
                                        }
                                    })}
                                    checkedOptions={model.selectedPrograms}
                                    onChange={(o) => setModel({ ...model, selectedPrograms: o })} />
                            </div>
                        </div>}
                        {showPrograms && <div className="program-enrollment-panel hide-on-print">
                            <div className="row margin-vertical-4 align-items-start">
                                <div className="column-large-5">
                                    <CheckboxGroup id="available-programs"
                                        label="Programs"
                                        options={programs.map(p => {
                                            return {
                                                label: p.name,
                                                value: p.id
                                            }
                                        })}
                                        checkedOptions={model.selectedPrograms}
                                        onChange={(o) => setModel({ ...model, selectedPrograms: o })} />
                                </div>
                                <div className="column-large-7">
                                    <div className="program-enrollment-targets">
                                        {householdProgramEnrollmentEnabled && <div className="household-member-option-help">
                                            Household members are eligible when they are linked to an existing participant or marked to create as a participant.
                                        </div>}
                                        {model.errors && model.errors.enrolled.length !== 0 && <ErrorMessages messages={model.errors.enrolled} />}
                                        {model.selectedPrograms.length === 0 && <div className="program-enrollment-empty">
                                            Select one or more programs to choose who should be enrolled.
                                        </div>}
                                        {model.selectedPrograms.length !== 0 && <div className="program-enrollment-grid">
                                            <div className="program-enrollment-grid-header">Participant</div>
                                            <div className="program-enrollment-grid-header">Status</div>
                                            <div className="program-enrollment-grid-header">Enrollment Date</div>
                                            {getProgramEnrollmentParticipantRows().map(row => {
                                                const selection = getProgramEnrollmentSelection(row.key);
                                                return <React.Fragment key={row.key}>
                                                    <div className="program-enrollment-grid-cell">
                                                        <Checkbox id={`program-enrollment-participant-${row.key}`}
                                                            label={row.label}
                                                            containerClassName="unboxed household-member-compact-checkbox"
                                                            checked={selection.selected}
                                                            onChange={(checked) => handleProgramEnrollmentParticipantSelected(row.key, checked)} />
                                                    </div>
                                                    <div className="program-enrollment-grid-cell">
                                                        <span className="program-enrollment-status">{row.type}</span>
                                                    </div>
                                                    <div className="program-enrollment-grid-cell">
                                                        <Input id={`program-enrollment-date-${row.key}`}
                                                            onChange={(e) => handleProgramEnrollmentDateChanged(row.key, e)}
                                                            value={selection.enrollmentDate}
                                                            type="date"
                                                            label="" />
                                                    </div>
                                                </React.Fragment>
                                            })}
                                        </div>}
                                    </div>
                                </div>
                            </div>
                        </div>}
                        {props.isEmbedded && <>
                            {!props.id && possibleDuplicates && possibleDuplicates.length !== 0 && <div className="row margin-top-5 hide-on-print align-items-center">
                                <div className="column-auto">
                                    <Button id="go-to-possible-duplicates"
                                        text="View Possible Duplicates"
                                        className="warning"
                                        onClick={goToPossibleDuplicates} />
                                </div>
                            </div>}
                            <div className="row align-items-center margin-top-5 justify-content-between">
                                <div className="column-auto">
                                    <div className="row">
                                        <div className="column-auto">
                                            <Button id="save-and-create-new-button"
                                                text="Save & Close"
                                                className="flat"
                                                onClick={handleCreating}
                                                loading={submittingAndCreateNew}
                                                disabled={submitting} />
                                        </div>
                                        <div className="column-auto">
                                            <Button id="save-and-go-to-profile-button"
                                                type="submit"
                                                text={!props.id ? "Save & Go to Profile" : "Save Participant"}
                                                loading={submitting && !submittingAndCreateNew}
                                                disabled={submitting} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>}
                        {!props.isEmbedded && <>
                            <div className="row justify-content-between margin-top-5 hide-on-print align-items-center">
                                {!props.id && possibleDuplicates && possibleDuplicates.length !== 0 && <div className="column-auto">
                                    <Button id="go-to-possible-duplicates"
                                        text="View Possible Duplicates"
                                        className="warning"
                                        onClick={goToPossibleDuplicates} />
                                </div>}
                                <div className="column">
                                    <div className="row align-items-center justify-content-end">
                                        <div className="column-auto">
                                            <div className="row">
                                                {!props.id && <div className="column-auto">
                                                    <Button id="save-and-create-new-button"
                                                        text="Save & Create New"
                                                        className="flat"
                                                        onClick={handleCreating}
                                                        loading={submittingAndCreateNew}
                                                        disabled={submitting} />
                                                </div>}
                                                <div className="column-auto">
                                                    <Button id="save-and-go-to-profile-button"
                                                        type="submit"
                                                        text={!props.id ? "Save & Go to Profile" : "Save Participant"}
                                                        loading={submitting && !submittingAndCreateNew}
                                                        disabled={submitting} />
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>}
                        {showHouseholdEditingSection && <>
                            <h2 className="section-header show-on-print break-before">Household and Family Members</h2>
                            <div className="show-on-print">
                                {model.householdMembers && model.householdMembers.length !== 0 && model.householdMembers.map((hhm, i) => {
                                    return getHouseholdMember(hhm, i);
                                })}
                                {(!model.householdMembers || model.householdMembers.length == 0) && printableHouseholdMembers.map((hhm, i) => {
                                    return getHouseholdMember(hhm, i);
                                })}
                            </div>
                        </>}
                    </form>
                </div>
            </div>
        </div >
    )
}

export default ParticipantForm;
